import { memo, useMemo } from 'react';
import { Field } from 'react-final-form';

import { validateMaxLength, validateRequiredFn } from 'shared/functions';
import { BaseFileTypeDto } from '@eva/types/dto';
import { FileUploadField } from 'shared/ui/FileUploadField';
import { Input } from 'shared/ui/Input';
import { DatePicker } from 'shared/ui/DatePicker/DatePicker';
import { SelectField } from 'shared/ui/SelectField/SelectField';

import { dropzoneContentType } from 'components/Dropzone';

import type { UploadFileBaseOptions, UploadFileBaseValues } from '../types';

// eslint-disable-next-line import/no-cycle
import { buildOptionGroups, getActiveFileTypes, type FileTypeOption } from './functions';
import { formatFileTypeLabel, formatGroupLabel } from './renderers';

const validateFileTitle = (title: string) => {
  const error = validateRequiredFn(title);

  if (error) {
    return error;
  }

  return validateMaxLength(title, 255);
};

export interface FormUploadFileValues<FT extends BaseFileTypeDto = BaseFileTypeDto> extends UploadFileBaseValues {
  title?: string;
  verified?: boolean;
  expiryDate?: string;
  issueDate?: string;
  fileType: FileTypeOption<FT>;
}

export interface FormUploadFileProps<FT> extends UploadFileBaseOptions<FormUploadFileValues> {
  canVerify?: boolean;
  updateMode?: boolean;
  fileTypes: FT[];
  handleAcceptFiles?: (file: []) => void;
}

const accept = {
  'text/csv': ['.csv'],
  'text/plain': ['.txt'],
  'application/pdf': ['.pdf'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'audio/*': ['.m2a', '.m3a', '.mp2', '.mp2a', '.mp3', '.mpga'],
  'video/*': ['.avi', '.mov', '.qt', '.mp4'],
  'application/msword': ['.doc'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.ms-powerpoint': ['.pot', '.ppa', '.pps', '.ppt', '.pwz'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xslx'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
};

const getUploadFileLabel = () => <div className="text-primary margin-top">{translate('Upload a file')}</div>;

const getUploadFileDescription = () =>
  translate('Only doc, docx, pdf, txt, jpg, png, mov, mp4, xlsx, ppt, mp3, xls, csv; 5Mb maximum.');

const FormUploadFile = <FT extends BaseFileTypeDto>({
  initialValues,
  canVerify,
  updateMode,
  fileTypes,
  handleAcceptFiles,
}: FormUploadFileProps<FT>) => {
  const options = useMemo(() => buildOptionGroups(getActiveFileTypes(fileTypes)), [fileTypes]);

  const dropzone = (
    <Field
      name="files"
      maxFiles={1}
      minSize={10}
      accept={accept}
      maxSize={5 * 1024 * 1024}
      disabled={updateMode}
      label={getUploadFileLabel()}
      valueByDefault={initialValues.files}
      contentType={dropzoneContentType.base64String}
      description={updateMode ? null : getUploadFileDescription()}
      component={FileUploadField}
      validate={validateRequiredFn}
      handleAcceptFiles={handleAcceptFiles}
    />
  );

  return (
    <div>
      <div className="row">
        <div className="col-xs-12">{dropzone}</div>
      </div>
      {updateMode && (
        <div className="row">
          <div className="col-xs-12">
            <Field
              clearable={false}
              name="title"
              label={translate('File title')}
              component={Input}
              validate={validateFileTitle}
            />
          </div>
        </div>
      )}
      <div className="row">
        <div className={canVerify ? 'col-xs-6' : 'col-xs-12'}>
          <Field
            clearable
            multi={false}
            name="fileType"
            label={translate('File type')}
            options={options}
            // @ts-expect-error:
            component={SelectField}
            formatGroupLabel={formatGroupLabel}
            formatOptionLabel={formatFileTypeLabel}
            validate={validateRequiredFn}
          />
        </div>
        {canVerify && (
          <div className="col-xs-6" style={{ marginTop: '32px' }}>
            <label className="control-label">
              <Field
                type="checkbox"
                name="verified"
                formClass="inline-div"
                valueClass="inline-div"
                component={Input}
                labelClass="hide"
              />
              &nbsp;&nbsp;{translate('Mark as verified')}
            </label>
          </div>
        )}
      </div>
      <div className="row">
        <div className="col-xs-6">
          <div className="form-group">
            <label className="control-label">{translate('Issue date')}</label>
            <Field name="issueDate" initiallyempty="true" component={DatePicker} />
          </div>
        </div>
        <div className="col-xs-6">
          <div className="form-group">
            <label className="control-label">{translate('Expiry date')}</label>
            <Field name="expiryDate" initiallyempty="true" component={DatePicker} />
          </div>
        </div>
      </div>
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default memo(FormUploadFile);
